import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCreateSubscriptionMutation } from '../../redux/thunks/sp/createsubscription';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography, 
  TextField, 
  Divider, 
  Alert,
  useMediaQuery,
  useTheme,
  Stack,
  Card,
  CardContent
} from '@mui/material';

//@ replace in production
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ clientSecret, subscriptionId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [status, setStatus] = useState('');
  const [name, setName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setStatus("Processing payment...");
    setError(null);
    
    try {
      // Simple confirmation with redirect back to success page
      const { error: paymentError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/subscription-success?subscription_id=${subscriptionId}`,
          payment_method_data: {
            billing_details: {
              name: name || undefined,
              // Remove manual address - Stripe PaymentElement will handle it
            }
          }
        },
      });

      if (paymentError) {
        console.error("Payment failed:", paymentError.message);
        setError(paymentError.message);
        setStatus(`Payment failed`);
      }
      // If successful, the redirect will happen automatically
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred. Please try again.");
      setStatus(`Payment failed`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mt: { xs: 1, sm: 2 } }}>
        <Stack spacing={{ xs: 1.5, sm: 2 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            size={isMobile ? "small" : "medium"}
            sx={{ 
              mb: { xs: 1, sm: 2 },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }
            }}
          />
          
          <Box sx={{ 
            my: { xs: 1.5, sm: 2 },
            '& .p-Element': {
              fontSize: isMobile ? '14px' : '16px'
            }
          }}>
            <PaymentElement 
              options={{
                layout: {
                  type: 'tabs',
                  defaultCollapsed: false,
                  radios: false,
                  spacedAccordionItems: isMobile ? false : true
                },
                fields: {
                  billingDetails: 'auto' // This will show address fields automatically when needed
                }
              }}
            />
          </Box>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                my: { xs: 1.5, sm: 2 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
              {error}
            </Alert>
          )}
          
          <Typography 
            variant="body2" 
            sx={{ 
              my: { xs: 1.5, sm: 2 }, 
              color: 'text.secondary', 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              lineHeight: 1.4,
              textAlign: 'center'
            }}>
            Your payment information will be securely stored for future billing and plan changes.
          </Typography>
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={!stripe || processing}
            sx={{ 
              mt: { xs: 1.5, sm: 2 }, 
              mb: { xs: 1.5, sm: 2 },
              height: { xs: 44, sm: 48 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600
            }}
            fullWidth
          >
            {processing ? (
              <Box display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={isMobile ? 20 : 24} sx={{ mr: 1 }} color="inherit" />
                <span>Processing...</span>
              </Box>
            ) : (
              "Subscribe Now"
            )}
          </Button>
          
          {status && !error && (
            <Typography 
              sx={{ 
                mt: 1, 
                mb: 2, 
                textAlign: 'center',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
              {status}
            </Typography>
          )}
        </Stack>
      </Box>
    </form>
  );
};

const StripeCheckout = ({ plan }) => {
  const [clientSecret, setClientSecret] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [error, setError] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const userEmail = useSelector((state) => state.auth.user.email);
  const userToken = useSelector((state) => state.auth.token);
  const userId = useSelector((state) => state.auth.user._id);
  const displayName = useSelector((state) => state.auth.user.displayName);

  const [createSubscription, { isLoading }] = useCreateSubscriptionMutation();

  useEffect(() => {
    const fetchClientSecret = async () => {
      const payload = {
        auth: { token: userToken, authType: 'token' },
        userId,
        userEmail,
        plan: plan.priceName,
        displayName
      };
      try {
        const response = await createSubscription({ payload }).unwrap();
        
        if (!response.clientSecret) {
          throw new Error("No client secret returned from the server");
        }
        
        setClientSecret(response.clientSecret);
        
        // Store subscription ID for the redirect URL
        if (response.subscriptionId) {
          setSubscriptionId(response.subscriptionId);
        }
        
        if (response.customerId) {
          setCustomerId(response.customerId);
        }
        
        // Log response to help with debugging
        console.log("Subscription created successfully:", {
          subscriptionId: response.subscriptionId,
          customerId: response.customerId,
          clientSecret: response.clientSecret ? "Present" : "Missing",
        });
      } catch (err) {
        console.error("Error fetching clientSecret:", err);
        setError(err.message || "Failed to create subscription. Please try again.");
      }
    };

    fetchClientSecret();
  }, [userEmail, userToken, userId, createSubscription, plan]);

  const stripeOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        borderRadius: '8px',
        colorBackground: '#f5f5f5',
        fontSize: isMobile ? '14px' : '16px',
        spacingUnit: isMobile ? '4px' : '6px',
      },
      rules: {
        '.Input': {
          padding: isMobile ? '10px 8px' : '12px',
          fontSize: isMobile ? '14px' : '16px'
        },
        '.Label': {
          fontSize: isMobile ? '12px' : '14px',
          marginBottom: isMobile ? '4px' : '6px'
        }
      }
    },
  };

  if (error) {
    return (
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          color="primary"
          fullWidth
          onClick={() => window.location.reload()}
          sx={{ 
            height: { xs: 44, sm: 48 },
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (!clientSecret) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        py={{ xs: 4, sm: 6 }}>
        <CircularProgress size={isMobile ? 32 : 40} />
      </Box>
    );
  }

  // Check if plan has limited time pricing
  const hasLimitedOffer = plan.name === 'Premium' && plan.regularPrice && plan.limitedPrice && plan.regularPrice !== plan.limitedPrice;

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Plan Summary Card - Mobile optimized */}
      <Card 
        variant="outlined" 
        sx={{ 
          mb: { xs: 2, sm: 3 },
          bgcolor: hasLimitedOffer ? 'primary.50' : 'grey.50'
        }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            flexWrap="wrap"
            gap={1}>
            <Box>
              <Typography 
                variant="h6"
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {plan.name} Plan
              </Typography>
              {hasLimitedOffer && (
                <Typography 
                  variant="body2"
                  color="primary.main"
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    fontWeight: 600
                  }}>
                  🚀 Inaugural Launch Pricing
                </Typography>
              )}
            </Box>
            
            <Box textAlign="right">
              {hasLimitedOffer ? (
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      textDecoration: 'line-through', 
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' }
                    }}>
                    {plan.regularPrice}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    fontWeight="bold" 
                    color="error.main"
                    sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                    {plan.limitedPrice}
                  </Typography>
                </Stack>
              ) : (
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  color="primary"
                  sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                  {plan.price}
                </Typography>
              )}
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                {plan.priceName === 'Yearly' ? 'per year' : 'per month'}
              </Typography>
            </Box>
          </Box>
          
          {hasLimitedOffer && plan.savingsText && (
            <Box 
              sx={{ 
                mt: 1, 
                p: 1, 
                bgcolor: 'success.main', 
                borderRadius: 1,
                textAlign: 'center'
              }}>
              <Typography 
                variant="body2" 
                color="white" 
                fontWeight={600}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                {plan.savingsText} • Founding Member Special
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Divider sx={{ my: { xs: 2, sm: 2 } }} />
      
      <Elements stripe={stripePromise} options={stripeOptions}>
        <CheckoutForm 
          clientSecret={clientSecret} 
          subscriptionId={subscriptionId}
          customerId={customerId}
        />
      </Elements>
    </Box>
  );
};

export default StripeCheckout;